import React, {
  useContext, useEffect, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { appStore } from './state/app';
import CreateAcc from './page/CreateAcc/CreateAcc';
import Login from './page/Login';
import WelcomePage from './page/WelcomePage';
import RenderRoutes from './components/RenderRoutes';
import Layout from './Layouts/Layout';
import LaunchProject from './page/LaunchProject';
import ExistingProject from './page/ExistingProject';
import LayoutDash from './Layouts/LayoutDash/LayoutDash';
import LayoutOuter from './Layouts/LayoutOuter/LayoutOuter';
import ProjectPage from './page/ProjectPage';
import EditPage from './page/EditPage';
import DataUpload from './page/DataUpload/DataUpload';
import FieldAgentsAssignments from './page/FieldAgentsAssignments';
import MyFieldAgents from './page/MyFieldAgents';
import TermsConditions from './page/TermsConditions';
import PrivateNotice from './page/PrivateNotice';
import Loader from './components/Loader/Loader';
import ReadyPage from './components/ReadyPage';
import storage from './utils/storage';
import { checkingLogin, expireToken } from './utils/login';
import LayoutSimple from './Layouts/Layout/LayoutSimple';
import ErrorBoundaries from './Layouts/ErrorBoundaries';
import getProjectOperator from './services/getProjectOperator';
import Settings from './page/Settings';
import IpfsImagePreview from './page/IpfsImagePreview/IpfsImagePreview';
import { clearKeyNearLocalStorage } from './utils/helpers';

const Terms = ({ children }) => {
  const { state, update } = useContext(appStore);
  const history = useHistory();
  const { account, app } = state;
  const { profile, errors } = app;
  const [returnElem, setReturnElem] = useState(profile && profile.terms && children);

  useEffect(() => {
    let isUnmounted = false;

    const runAuthFlow = async () => {
      const hasAccount = !!account?.accountId;
      const token = storage.get('token');
      const logoutInProgress = !!storage.get('logoutInProgress');

      const isExpiredToken = !!(token && expireToken(token));
      if (logoutInProgress && !hasAccount) {
        return;
      }

      if (isExpiredToken || !hasAccount) {
        storage.del('token');
        storage.del('isSignIn');
        storage.del('postLoginRedirect');
        storage.del('emailUser');
        if (isExpiredToken) {
          storage.set('forceWalletReconnect', true);
        } else {
          storage.del('forceWalletReconnect');
        }
        clearKeyNearLocalStorage();
        update('app.errors', '');
        update('app.profile', {});
        update('wallet.signedIn', false);
        update('', { account: null });
        history.push('/login');
        return;
      }

      if (token && !expireToken(token)) {
        const profiles = await getProjectOperator();
        if (profiles) {
          update('app.profile', {
            ...profiles.organization, verifiedDate: profiles?.verified_date,
          });
        }
        if (!isUnmounted) setReturnElem(children);
        return;
      }

      if (hasAccount) {
        storage.set('isSignIn', true);
        await checkingLogin(account, history, () => {
          if (!isUnmounted) setReturnElem(children);
        }, true, update);
        return;
      }

      history.push('/login');
    };

    runAuthFlow();

    return () => {
      isUnmounted = true;
    };
  }, [account]);

  return (
    <>
      {Object.keys(errors).length > 0 && <ErrorBoundaries />}
      {returnElem || <Loader customClass="lds-ring__big" />}
    </>
  );
};

const routes = [
  {
    path: '/login',
    key: 'LOGIN',
    exact: true,
    component: () => (
      <LayoutOuter>
        <Login />
      </LayoutOuter>
    ),
  },
  {
    path: '/welcome',
    key: 'WELCOME',
    exact: true,
    component: () => (
      <LayoutOuter>
        <WelcomePage />
      </LayoutOuter>
    ),
  },
  {
    path: '/create-account',
    key: 'CREATE_ACC',
    exact: true,
    component: () => (
      <LayoutOuter>
        <CreateAcc />
      </LayoutOuter>
    ),
  },
  {
    path: '/ready-page',
    key: 'READE_PAGE',
    exact: true,
    component: () => (
      <Layout>
        <ReadyPage />
      </Layout>
    ),
  },
  {
    path: '/terms-conditions',
    key: 'TERMS_CONDITIONS',
    exact: true,
    component: () => (
      <TermsConditions />
    ),
  },
  {
    path: '/privacy-notice',
    key: 'PRIVACY_NOTICE',
    exact: true,
    component: () => (
      <PrivateNotice />
    ),
  },
  {
    path: '/ipfs-image',
    key: 'IPFS_IMAGE_PREVIEW',
    exact: true,
    component: () => (
      <Layout>
        <IpfsImagePreview />
      </Layout>
    ),
  },
  {
    path: '/',
    key: 'HOME',
    component: (props) => (
      <Terms>
        <RenderRoutes {...props} />
      </Terms>
    ),
    routes: [
      {
        path: '/',
        key: 'DASHBOARD',
        exact: true,
        component: () => (
          <Terms>
            <LayoutDash>
              <ExistingProject />
            </LayoutDash>
          </Terms>
        ),
      },
      {
        path: '/project/:nameId',
        key: 'PROJECT',
        component: () => (
          <LayoutDash>
            <ProjectPage />
          </LayoutDash>
        ),
      },
      {
        path: '/create-project',
        key: 'PROJECT_REGISTER',
        exact: true,
        component: () => (
          <LaunchProject />
        ),
      },
      {
        path: '/edit/:nameId',
        key: 'EDIT',
        exact: true,
        component: () => (
          <LayoutSimple>
            <EditPage />
          </LayoutSimple>
        ),
      },
      {
        path: '/data-upload/:nameId',
        key: 'DATA_UPLOAD',
        exact: true,
        component: () => (
          <DataUpload />
        ),
      },
      {
        path: '/field-agents',
        key: 'FIELD_AGENTS',
        exact: true,
        component: () => (
          <LayoutDash>
            <FieldAgentsAssignments />
          </LayoutDash>
        ),
      },
      {
        path: '/settings',
        key: 'SETTINGS',
        component: () => (
          <LayoutDash>
            <Settings />
          </LayoutDash>
        ),
      },
      {
        path: '/my-field-agents',
        key: 'MY_FIELD_AGENTS',
        exact: true,
        component: () => (
          <LayoutDash>
            <MyFieldAgents />
          </LayoutDash>
        ),
      },
    ],
  },
];

export default routes;
